import type { HistoryResponse, LeagueBundle, TeamRecord } from '../../shared/types';
import { isPreseason, lastLeagueWeek } from './league';

/** How a manager finished the most recently completed season. */
export interface PriorFinish {
  season: string;
  place: number;
  record: TeamRecord;
}

/**
 * Each CURRENT roster's finish in the last completed season.
 *
 * Joined on `owner_id`, never on roster_id. Sleeper mints a brand-new league
 * every August and renumbers the rosters with it, so a roster-keyed join would
 * silently hand last season's finish to whoever inherited the number. Managers
 * who were not in the league last season are simply absent from the map, which
 * is the honest answer -- they have no prior finish, and coercing them to last
 * place would invent a result.
 */
export function priorFinishes(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
): Map<number, PriorFinish> {
  const out = new Map<number, PriorFinish>();
  const last = history?.seasons[0];
  if (!last || last.placements.length === 0) return out;

  const byOwner = new Map(
    last.placements.filter((p) => p.owner_id).map((p) => [p.owner_id as string, p]),
  );

  for (const t of bundle.teams) {
    const p = t.owner_id ? byOwner.get(t.owner_id) : undefined;
    if (p) out.set(t.roster_id, { season: last.season, place: p.place, record: p.record });
  }
  return out;
}

/**
 * The week the home page should lead with.
 *
 * Two calendar edges both produce a phantom week if `display_week` is trusted
 * raw. Before kickoff it points at an NFL preseason week that has no fantasy
 * games (it read 2 in August 2026), and after the league's final game Sleeper
 * keeps scoring lineups indefinitely while pairing nobody -- 2025 week 18 came
 * back with all ten rosters scoring and `matchup_id: null` on every row. Clamp
 * to the last week the bracket can occupy so the page never renders a set of
 * opponent-less teams.
 */
export function focusWeek(bundle: LeagueBundle): number {
  if (isPreseason(bundle)) return 1;
  return Math.min(Math.max(1, bundle.state.display_week), lastLeagueWeek(bundle.league));
}
